import { TypedEventEmitter } from './event-emitter';
import { JanusTransport } from './transport';
import { JanusSession } from './session';
import { WebRTCWrapper } from './webrtc';
import { AsyncMutex } from './mutex';
import { isRetryableStreamingError, DEFAULT_CONFIG as DEFAULTS } from './types';
export class StreamingPlugin extends TypedEventEmitter {
    constructor(config) {
        super({ debug: config.debug });
        this.transport = null;
        this.session = null;
        this.handle = null;
        this.webrtc = null;
        this.oldWebrtc = null;
        this.state = 'disconnected';
        this.iceState = 'new';
        this.streams = [];
        this.currentStream = null;
        this.lifecycleMutex = new AsyncMutex();
        this.streamAbort = null;
        this.iceDisconnectedTimer = null;
        this.mediaStream = null;
        this.disconnecting = false;
        this.reconnecting = false;
        this.reconnectAttempt = 0;
        this.config = { ...DEFAULTS, ...config };
    }
    get currentState() { return this.state; }
    get currentIceState() { return this.iceState; }
    get streamList() { return this.streams; }
    get currentMediaStream() { return this.mediaStream; }
    get activeStreamId() { return this.currentStream?.id ?? null; }
    sendData(data) { this.webrtc?.sendData(data); }
    setState(newState) {
        if (this.state === newState || (this.disconnecting && newState !== 'disconnected'))
            return;
        this.state = newState;
        this.emit('stateChange', newState);
        this.addLog('info', `State: ${newState}`);
    }
    setIceState(newState) {
        if (this.iceState === newState)
            return;
        this.iceState = newState;
        this.emit('iceStateChange', newState);
    }
    addLog(level, message) {
        this.emit('log', { timestamp: Date.now(), level, message });
    }
    teardownWebRTC() {
        this.clearIceDisconnectedTimer();
        this.webrtc?.dispose();
        this.webrtc = null;
        this.oldWebrtc?.dispose();
        this.oldWebrtc = null;
        this.mediaStream = null;
        this.setIceState('new');
    }
    checkPluginResponse(response, context) {
        if (response.plugindata?.data) {
            const data = response.plugindata.data;
            if ('error_code' in data && typeof data.error_code === 'number') {
                throw new PluginError(data.error_code, data.error ?? 'Unknown error', context);
            }
        }
    }
    async performSdpExchange(webrtc, jsep, signal) {
        signal?.throwIfAborted();
        await webrtc.setRemoteDescription({ type: jsep.type, sdp: jsep.sdp });
        signal?.throwIfAborted();
        const answer = await webrtc.createAnswer();
        signal?.throwIfAborted();
        const startResponse = await this.handle.sendMessage({ request: 'start' }, { type: 'answer', sdp: answer.sdp }, { waitForEvent: true, signal });
        if (startResponse.janus === 'error') {
            const err = startResponse;
            throw new Error(`Start failed: ${err.error.reason}`);
        }
    }
    async connect(signal) {
        const release = await this.lifecycleMutex.acquire(signal);
        try {
            signal?.throwIfAborted();
            this.setState('connecting');
            this.transport = new JanusTransport(this.config.server, {
                longPollTimeoutMs: this.config.longPollTimeoutMs, headers: this.config.headers, debug: this.config.debug,
            });
            this.session = new JanusSession(this.transport, {
                keepaliveIntervalMs: this.config.keepaliveIntervalMs,
                keepaliveFailureThreshold: this.config.keepaliveFailureThreshold,
                debug: this.config.debug,
            });
            this.session.on('error', (err) => this.handleSessionError(err));
            this.session.on('handleEvent', ({ handleId, event }) => {
                if (handleId === this.handle?.id)
                    this.handlePluginEvent(event);
            });
            this.session.on('handleTrickle', ({ handleId, candidate }) => {
                if (handleId === this.handle?.id)
                    this.handleTrickleCandidate(candidate);
            });
            this.session.on('handleHangup', ({ handleId, reason }) => {
                if (handleId === this.handle?.id)
                    this.handleHangup(reason);
            });
            this.session.on('handleWebrtcUp', ({ handleId }) => {
                if (handleId === this.handle?.id) {
                    this.addLog('info', 'WebRTC PeerConnection is up');
                    this.emit('webrtcUp', undefined);
                }
            });
            this.session.on('handleSlowLink', ({ handleId, uplink, lost, mid }) => {
                if (handleId === this.handle?.id) {
                    this.addLog('warn', `Slow link: ${lost} lost ${uplink ? 'sending' : 'receiving'} mid ${mid ?? '?'}`);
                    this.emit('slowLink', { uplink, lost, mid });
                }
            });
            await this.session.create(signal);
            signal?.throwIfAborted();
            this.setState('connected');
            this.handle = await this.session.attach('janus.plugin.streaming', signal);
            signal?.throwIfAborted();
            this.setState('attached');
            this.addLog('info', `Handle attached: ${this.handle.id}`);
            await this.fetchStreams(signal);
        }
        catch (err) {
            this.setState('error');
            this.emit('error', { type: 'system', message: err instanceof Error ? err.message : String(err), retryable: false });
            throw err;
        }
        finally {
            release();
        }
    }
    async fetchStreams(signal) {
        if (!this.handle)
            return;
        const response = await this.handle.sendMessage({ request: 'list' }, undefined, { waitForEvent: false, signal });
        if (response.janus === 'success' && 'plugindata' in response) {
            const plugindata = response.plugindata;
            if (plugindata?.data?.list) {
                this.streams = plugindata.data.list;
                this.emit('streams', this.streams);
                this.addLog('info', `Found ${this.streams.length} stream(s)`);
            }
        }
    }
    async watch(options) {
        this.streamAbort?.abort();
        this.streamAbort = new AbortController();
        const signal = options.signal
            ? combineSignals(options.signal, this.streamAbort.signal)
            : this.streamAbort.signal;
        try {
            if (this.state !== 'attached' && this.state !== 'streaming') {
                throw new Error(`Cannot watch in state: ${this.state}`);
            }
            if (this.state === 'streaming')
                await this.stopStream(signal);
            signal.throwIfAborted();
            this.setState('watching');
            const body = { request: 'watch', id: options.id };
            if (options.pin)
                body.pin = options.pin;
            const response = await this.handle.sendMessage(body, undefined, { waitForEvent: true, signal });
            signal.throwIfAborted();
            if (response.janus === 'error') {
                const err = response;
                this.handlePluginError(err.error.code, err.error.reason);
                return;
            }
            const eventResp = response;
            try {
                this.checkPluginResponse(eventResp, 'watch');
            }
            catch (err) {
                if (err instanceof PluginError) {
                    this.handlePluginError(err.code, err.message);
                    return;
                }
                throw err;
            }
            if (!eventResp.jsep)
                throw new Error('No JSEP offer received from Janus');
            this.currentStream = { id: options.id, pin: options.pin };
            this.webrtc = new WebRTCWrapper({
                iceServers: this.config.iceServers, statsIntervalMs: this.config.statsIntervalMs, debug: this.config.debug,
            });
            this.setupWebRTCListeners(this.webrtc);
            this.webrtc.createPeerConnection();
            await this.performSdpExchange(this.webrtc, eventResp.jsep, signal);
            this.webrtc.startStatsPolling();
            this.addLog('info', 'WebRTC negotiation complete');
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                this.addLog('info', 'Watch aborted');
                return;
            }
            if (this.state === 'watching')
                this.setState('attached');
            throw err;
        }
    }
    setupWebRTCListeners(webrtc) {
        webrtc.on('track', (stream) => { this.mediaStream = stream; this.emit('stream', stream); });
        webrtc.on('iceCandidate', (candidate) => {
            if (!this.handle)
                return;
            if (candidate) {
                this.handle.sendTrickle({
                    candidate: candidate.candidate, sdpMid: candidate.sdpMid ?? undefined,
                    sdpMLineIndex: candidate.sdpMLineIndex ?? undefined,
                }).catch((err) => this.addLog('warn', `Trickle send failed: ${err}`));
            }
            else {
                this.handle.sendTrickle({ completed: true }).catch(() => { });
            }
        });
        webrtc.on('iceStateChange', (state) => { this.setIceState(state); this.handleIceStateChange(state); });
        webrtc.on('stats', (stats) => this.emit('stats', stats));
        webrtc.on('dataChannelOpen', (ch) => { this.dataChannel = ch; this.emit('dataChannelOpen', ch); });
        webrtc.on('dataChannelClose', () => { this.dataChannel = null; this.emit('dataChannelClose', undefined); });
    }
    handleIceStateChange(state) {
        this.clearIceDisconnectedTimer();
        switch (state) {
            case 'connected':
            case 'completed':
                if (this.state === 'watching')
                    this.setState('streaming');
                break;
            case 'disconnected':
                this.addLog('warn', 'ICE disconnected — waiting 5s for recovery');
                this.iceDisconnectedTimer = setTimeout(() => {
                    if (this.webrtc?.iceState === 'disconnected') {
                        this.addLog('warn', 'ICE still disconnected after 5s — attempting restart');
                        this.attemptIceRestart();
                    }
                }, this.config.iceDisconnectedTimeoutMs);
                break;
            case 'failed':
                this.addLog('error', 'ICE failed — attempting restart');
                this.attemptIceRestart();
                break;
        }
    }
    clearIceDisconnectedTimer() {
        if (this.iceDisconnectedTimer) {
            clearTimeout(this.iceDisconnectedTimer);
            this.iceDisconnectedTimer = null;
        }
    }
    async attemptIceRestart() {
        if (!this.handle || !this.currentStream) {
            this.emit('error', { type: 'system', message: 'Cannot restart ICE: no active stream', retryable: false });
            return;
        }
        // Tier 1: configure restart → Tier 2: apply new offer to existing PC
        try {
            this.addLog('info', 'ICE restart tier 1: configure restart');
            const response = await this.handle.sendMessage({ request: 'configure', restart: true }, undefined, { waitForEvent: true });
            const eventResp = response;
            if (eventResp.jsep) {
                try {
                    await this.performSdpExchange(this.webrtc, eventResp.jsep);
                    this.addLog('info', 'ICE restart tier 2 succeeded');
                    return;
                }
                catch (tier2Err) {
                    this.addLog('warn', `ICE restart tier 2 failed: ${tier2Err}`);
                }
            }
        }
        catch (tier1Err) {
            this.addLog('warn', `ICE restart tier 1 failed: ${tier1Err}`);
        }
        // Tier 3: full teardown + re-watch
        try {
            this.addLog('info', 'ICE restart tier 3: full renegotiation');
            await this.fullRenegotiation();
        }
        catch (tier3Err) {
            this.addLog('error', `ICE restart tier 3 failed: ${tier3Err}`);
            this.setState('error');
            this.emit('error', { type: 'system', message: `ICE restart failed: ${tier3Err}`, retryable: false });
        }
    }
    async fullRenegotiation() {
        if (!this.handle || !this.currentStream)
            return;
        const oldWebrtc = this.webrtc;
        this.oldWebrtc = oldWebrtc;
        const newWebrtc = new WebRTCWrapper({
            iceServers: this.config.iceServers, statsIntervalMs: this.config.statsIntervalMs, debug: this.config.debug,
        });
        this.webrtc = newWebrtc;
        this.setupWebRTCListeners(newWebrtc);
        newWebrtc.createPeerConnection();
        try {
            await this.handle.sendMessage({ request: 'stop' }, undefined, { waitForEvent: true }).catch(() => { });
            const body = { request: 'watch', id: this.currentStream.id };
            if (this.currentStream.pin)
                body.pin = this.currentStream.pin;
            const response = await this.handle.sendMessage(body, undefined, { waitForEvent: true });
            if (response.janus === 'error') {
                const err = response;
                throw new Error(`Re-watch failed: ${err.error.reason} (code ${err.error.code})`);
            }
            const eventResp = response;
            this.checkPluginResponse(eventResp, 'Re-watch');
            if (!eventResp.jsep)
                throw new Error('No JSEP offer on renegotiation');
            await this.performSdpExchange(newWebrtc, eventResp.jsep);
            newWebrtc.startStatsPolling();
            this.addLog('info', 'Full renegotiation succeeded');
            oldWebrtc?.dispose();
            this.oldWebrtc = null;
        }
        catch (err) {
            newWebrtc.dispose();
            oldWebrtc?.dispose();
            this.oldWebrtc = null;
            this.webrtc = null;
            throw err;
        }
    }
    async switchStream(options) {
        this.streamAbort?.abort();
        this.streamAbort = new AbortController();
        const signal = options.signal
            ? combineSignals(options.signal, this.streamAbort.signal)
            : this.streamAbort.signal;
        try {
            if (this.state !== 'streaming')
                throw new Error(`Cannot switch in state: ${this.state}`);
            const body = { request: 'switch', id: options.id };
            if (options.pin)
                body.pin = options.pin;
            signal.throwIfAborted();
            const response = await this.handle.sendMessage(body, undefined, { waitForEvent: true, signal });
            if (response.janus === 'error') {
                const err = response;
                this.handlePluginError(err.error.code, err.error.reason);
                return;
            }
            const eventResp = response;
            try {
                this.checkPluginResponse(eventResp, 'switch');
            }
            catch (err) {
                if (err instanceof PluginError) {
                    this.handlePluginError(err.code, err.message);
                    return;
                }
                throw err;
            }
            if (eventResp.jsep)
                await this.performSdpExchange(this.webrtc, eventResp.jsep, signal);
            this.currentStream = { id: options.id, pin: options.pin };
            this.addLog('info', `Switched to stream ${options.id}`);
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
                return;
            throw err;
        }
    }
    async stop(signal) {
        this.streamAbort?.abort();
        this.streamAbort = null;
        try {
            await this.stopStream(signal);
            if (this.state === 'streaming' || this.state === 'watching')
                this.setState('attached');
        }
        catch { /* Best effort */ }
    }
    async stopStream(signal) {
        this.clearIceDisconnectedTimer();
        this.webrtc?.closePeerConnection();
        this.oldWebrtc?.dispose();
        this.oldWebrtc = null;
        this.mediaStream = null;
        this.setIceState('new');
        this.currentStream = null;
        if (this.handle) {
            await this.handle.sendMessage({ request: 'stop' }, undefined, { waitForEvent: false, signal }).catch(() => { });
        }
    }
    async disconnect(signal) {
        this.disconnecting = true;
        let release;
        try {
            release = await this.lifecycleMutex.acquire(signal);
        }
        catch (err) {
            this.disconnecting = false;
            throw err;
        }
        try {
            this.streamAbort?.abort();
            this.streamAbort = null;
            this.teardownWebRTC();
            if (this.handle && this.session?.isConnected) {
                await this.handle.sendMessage({ request: 'stop' }, undefined, { waitForEvent: false }).catch(() => { });
                await this.handle.detach().catch(() => { });
            }
            this.handle = null;
            if (this.session) {
                await this.session.destroy(signal).catch(() => { });
                this.session.dispose();
                this.session = null;
            }
            if (this.transport) {
                this.transport.destroy();
                this.transport = null;
            }
            this.currentStream = null;
            this.streams = [];
            this.setIceState('new');
            this.setState('disconnected');
            this.disconnecting = false;
        }
        finally {
            release();
        }
    }
    handlePluginEvent(event) {
        if (!event.plugindata?.data)
            return;
        const data = event.plugindata.data;
        if ('error_code' in data && typeof data.error_code === 'number') {
            this.handlePluginError(data.error_code, data.error);
            return;
        }
        if (event.jsep && this.webrtc)
            this.handleIncomingOffer(event.jsep);
    }
    async handleIncomingOffer(jsep) {
        if (!this.webrtc || !this.handle)
            return;
        try {
            await this.performSdpExchange(this.webrtc, jsep);
        }
        catch (err) {
            this.addLog('error', `Failed to handle incoming offer: ${err}`);
        }
    }
    handleTrickleCandidate(candidate) {
        this.webrtc?.addIceCandidate('completed' in candidate && candidate.completed === true ? null : candidate);
    }
    handleHangup(reason) {
        this.addLog('warn', `Hangup: ${reason}`);
        this.webrtc?.closePeerConnection();
        this.mediaStream = null;
        this.setIceState('new');
        if (this.state === 'streaming' || this.state === 'watching')
            this.setState('attached');
    }
    handlePluginError(code, message) {
        const retryable = isRetryableStreamingError(code);
        if (this.state === 'watching')
            this.webrtc?.closePeerConnection();
        this.setState(retryable ? 'attached' : 'error');
        this.emit('error', { type: 'plugin', code, message: message ?? 'Unknown error', retryable });
        this.addLog('error', `Plugin error ${code}: ${message}`);
    }
    handleSessionError(err) {
        this.addLog('error', `Session error: ${err.message}`);
        if (!this.disconnecting)
            this.attemptReconnect();
    }
    async attemptReconnect() {
        if (this.reconnecting || this.disconnecting)
            return;
        this.reconnecting = true;
        const savedStream = this.currentStream ? { ...this.currentStream } : null;
        for (this.reconnectAttempt = 0; this.reconnectAttempt < this.config.reconnectAttempts; this.reconnectAttempt++) {
            try {
                const delay = this.config.reconnectBackoffMs * Math.pow(2, this.reconnectAttempt);
                this.addLog('info', `Reconnect attempt ${this.reconnectAttempt + 1}/${this.config.reconnectAttempts} in ${delay}ms`);
                await new Promise((r) => setTimeout(r, delay));
                if (this.disconnecting)
                    break;
                this.teardownWebRTC();
                this.handle = null;
                this.setState('connecting');
                await this.session.reconnect();
                if (this.disconnecting)
                    break;
                this.setState('connected');
                this.handle = await this.session.attach('janus.plugin.streaming');
                if (this.disconnecting)
                    break;
                this.setState('attached');
                this.addLog('info', `Reconnected, handle: ${this.handle.id}`);
                await this.fetchStreams();
                if (savedStream) {
                    this.addLog('info', `Re-watching stream ${savedStream.id}`);
                    this.reconnecting = false;
                    await this.watch({ id: savedStream.id, pin: savedStream.pin });
                }
                this.reconnecting = false;
                this.reconnectAttempt = 0;
                return;
            }
            catch (err) {
                this.addLog('warn', `Reconnect attempt ${this.reconnectAttempt + 1} failed: ${err}`);
            }
        }
        this.reconnecting = false;
        this.reconnectAttempt = 0;
        this.setState('error');
        this.emit('error', { type: 'system', message: `Reconnect failed after ${this.config.reconnectAttempts} attempts`, retryable: false });
    }
    dispose() {
        this.streamAbort?.abort();
        this.teardownWebRTC();
        this.session?.dispose();
        this.transport?.destroy();
        this.removeAllListeners();
    }
}
class PluginError extends Error {
    constructor(code, message, context) {
        super(`${context}: ${message} (code ${code})`);
        this.code = code;
    }
}
function combineSignals(a, b) {
    const controller = new AbortController();
    if (a.aborted || b.aborted) {
        controller.abort();
        return controller.signal;
    }
    const onAbort = () => {
        a.removeEventListener('abort', onAbort);
        b.removeEventListener('abort', onAbort);
        controller.abort();
    };
    a.addEventListener('abort', onAbort, { once: true });
    b.addEventListener('abort', onAbort, { once: true });
    return controller.signal;
}
